// ToDo: WW-7459 Move to shared shadow folder
import styles from '../../../../../../erp/components/tab-panels/positions/components/position-picker/PositionPicker.module.scss';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { useObservableState } from 'observable-hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { TableStateBagProvider } from '@work4all/components';
import { ResizableArea } from '@work4all/components/lib/components/ResizableArea/ResizableArea';
import {
  ColumnVisibilityContext,
  useColumnVisibilityManager,
} from '@work4all/components/lib/dataDisplay/basic-table/hooks/useColumnVisibility';
import { LockSubscription } from '@work4all/components/lib/hooks/object-lock-subscription/LockSubscription';
import { IconButtonWithTooltip } from '@work4all/components/lib/input/actions/IconButtonWithTooltip';
import { ExpandButton } from '@work4all/components/lib/input/expand-button/ExpandButton';

import { CustomFieldsConfigProvider } from '@work4all/data/lib/custom-fields';
import { PrintBagProvider } from '@work4all/data/lib/hooks/useHandlePrint';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Currency } from '@work4all/models/lib/Classes/Currency.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { settings, useSetting } from '../../../../../../../../../../settings';
// ToDo: WW-7459 Move to shared shadow folder (4 below)
import { PositionGroupsTree } from '../../../../../../erp/components/tab-panels/positions/components/position-group-tree/PositionGroupsTree';
import { ArticleSearch } from '../../../../../../erp/components/tab-panels/positions/components/position-picker/components/ArticleSearch';
import { PositionMore } from '../../../../../../erp/components/tab-panels/positions/components/position-picker/components/PositionMore';
import { PositionsFilter } from '../../../../../../erp/components/tab-panels/positions/components/position-picker/components/PositionsFilter';

import { PositionArticlesTable } from './components/PositionArticlesTable';

export type IPositionPickerProps = {
  className?: string;
  onAddStandard: (article: Article) => void;
  onGroupDoubleClick?: (title: string) => void;
  disabled: boolean;
  customerId?: number;
  currency?: Currency;
};

export const PositionPicker = (props: IPositionPickerProps) => {
  const {
    className,
    onAddStandard,
    onGroupDoubleClick,
    disabled,
    customerId,
    currency,
  } = props;

  const { t } = useTranslation();

  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(null);

  const [searchString, setSearchString] = useObservableState(
    (input$) => input$.pipe(distinctUntilChanged(), debounceTime(200)),
    ''
  );

  const [selectedArticles, setSelectedArticles] = useState<Article[]>([]);

  const formattedSearchString = searchString.trim();

  const [tableInstance, setTableInstance] = useState<TableInstance | null>(
    null
  );

  const tableInstanceRef = useRef<TableInstance>(null);
  useEffect(() => {
    tableInstanceRef.current = tableInstance;
  }, [tableInstance]);
  const previewSize = useSetting(settings.leftAreaSize());

  const columnsVisibilityManager = useColumnVisibilityManager({
    tableInstanceRef,
  });

  const resetColumnsRef = useRef<() => void>(null);

  const serviceContractShowArticle = useSetting(
    settings.serviceContractShowArticle()
  );

  const shouldShowArticle = serviceContractShowArticle.value;

  return (
    <CustomFieldsConfigProvider entity={Entities.article}>
      <PrintBagProvider>
        <LockSubscription entity={Entities.article}>
          <TableStateBagProvider tableInstance={tableInstance}>
            <ColumnVisibilityContext.Provider value={columnsVisibilityManager}>
              <div
                className={clsx(styles.grid, className, {
                  [styles.pickerDisabled]: disabled,
                })}
              >
                <div
                  className={styles.toolbar}
                  style={{
                    padding: serviceContractShowArticle.value
                      ? '0.25rem 0 0.25rem 0.5rem'
                      : '0 0 0 0.5rem',
                  }}
                >
                  <ExpandButton
                    icon={
                      serviceContractShowArticle.value ? (
                        <ExpandMore />
                      ) : (
                        <ExpandLess />
                      )
                    }
                    onClick={() => {
                      serviceContractShowArticle.set(
                        !serviceContractShowArticle.value
                      );
                    }}
                    orientation="horizontal"
                  />

                  {shouldShowArticle ? (
                    <>
                      <Box flex="1"></Box>
                      <ArticleSearch
                        onChange={setSearchString}
                        disabled={disabled}
                      />

                      <div className={styles.actions}>
                        {selectedArticles.length ? (
                          <IconButtonWithTooltip
                            tooltip={t('COMMON.APPLY_SELECTION')}
                            icon={<CheckIcon />}
                            onClick={() => {
                              onAddStandard(selectedArticles[0]);
                            }}
                          />
                        ) : null}
                        <PositionsFilter
                          entity={Entities.article}
                          disabled={disabled}
                          tableInstanceRef={tableInstanceRef}
                        />
                        <PositionMore
                          resetColumns={resetColumnsRef.current}
                          disabled={disabled}
                          tableInstanceRef={tableInstanceRef}
                        />
                      </div>
                    </>
                  ) : null}
                </div>

                {shouldShowArticle ? (
                  <div className={styles.tableWrapper}>
                    <ResizableArea
                      handles="right"
                      className={styles.groupsContainer}
                      size={previewSize.value}
                      onResize={(size) => {
                        previewSize.set(size);
                      }}
                      maxWidth={800}
                      minWidth={100}
                    >
                      <div>
                        <PositionGroupsTree
                          disabled={disabled || !onGroupDoubleClick}
                          onSelect={(ids) => {
                            if (ids.length > 0) {
                              setSelectedGroupIds(ids);
                            } else {
                              setSelectedGroupIds(null);
                            }
                          }}
                          onNodeDoubleClick={(e) => {
                            if (disabled) return;
                            onGroupDoubleClick?.(e.target.innerText);
                          }}
                        />
                      </div>
                    </ResizableArea>

                    <div className={styles.tableContainer}>
                      <PositionArticlesTable
                        ref={setTableInstance}
                        searchString={formattedSearchString}
                        groups={selectedGroupIds}
                        onSelectedChange={setSelectedArticles}
                        onRowDoubleClick={(article) => onAddStandard(article)}
                        disabled={disabled}
                        setResetColumns={(resetColumns) =>
                          (resetColumnsRef.current = resetColumns)
                        }
                        customerId={customerId}
                        currency={currency}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </ColumnVisibilityContext.Provider>
          </TableStateBagProvider>
        </LockSubscription>
      </PrintBagProvider>
    </CustomFieldsConfigProvider>
  );
};
